import {
    Box,
    Button,
    Dialog,
    DialogActions,
    Drawer,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    Radio,
    RadioGroup,
    Skeleton,
    TextField,
    Theme,
    Typography,
    useTheme
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import {
    CartDialogIcon,
    CrossIcon,
    DialogCloseIcon,
    FilterIcon,
    SearchIcon,
} from "../../../../assets/SvgIcons";
import {
    useEffect,
    useState
} from "react";
import CustomFilter from "../../common/CustomFilter";
import {
    useDispatch,
    useSelector
} from "react-redux";
import {
    AmcFilter,
    AppliedRecommendationsFilters,
    GetRecommendationsDataResponseBody,
    RecommendationData,
    StatusFilter
} from "../../../../redux-store/types/investorProfile";
import {
    getAmcFilters,
    getRecommendationsData,
    getStatusFilters,
    setAppliedFundsFilters,
    setAppliedRecommendationsFilters,
    setExploreMutualFundsPageVisited,
    setRecommendationsActiveCategories,
    setSelectedSchemesObj
} from "../../../../redux-store/actions/investorProfile";
import { RootStateType } from "../../../../redux-store/reducers";
import { useDebounce } from "../../../../utils/useDebounce";
import dayjs from "../../../../utils/dayjs";
import {
    ColorMap,
    navigationData,
    PaginationObj
} from "../../types";
import RecommendationInfoCard from "./RecommendationInfoCard";
import { toINR } from "../../../../utils/utilityFunctions";
import TablePaginationComponent from "../../common/customTable/TablePaginationComponent";
import { useHistory } from "react-router";
import { HELDAWAY_PORTFOLIO_STATUS, MAX_SCHEMES_SELECTED, USER_ROLES } from "../../../../utils/constant";
import {
    addToCart,
    getCartItemsCount,
    setCartItemsCount
} from "../../../../redux-store/actions/cart";
import {
    AddToCartResponseBody,
    GetCartItemsCountResponseBody
} from "../../../../redux-store/types/cart";
import {
    AddToCartData,
    Categories,
    RecommendationDetails,
    RecommendationsData
} from "./types";
import { whiteSpaceOnlyRegex } from "../../../../utils/regex";
import { showError } from "../../../../redux-store/actions/auth";

const colorMap: ColorMap = {
    active: "success.800",
    inactive: "warning.500",
    "order failed": "error.500",
    "order placed": "success.800",
    "": "text.primary"
}

const primaryCategories = [
    { label: "New Purchase", count: 0 },
    { label: "Additional Purchase", count: 0 },
    { label: "SWP", count: 0 },
    { label: "STP", count: 0 },
    { label: "Switch", count: 0 },
    { label: "Redemption", count: 0 }
]

const secondaryCategories = [
    { label: "Lumpsum", count: 0 },
    { label: "SIP", count: 0 }
]

export default function Recommendations(props: { clientCode?: string }): JSX.Element {
    const { clientCode = '' } = props

    const theme = useTheme()
    const dispatch = useDispatch()

    const today = dayjs().startOf('day');

    const {
        appliedRecommendationsFilters,
        recommendationsActiveCategories
    } = useSelector((store: RootStateType) => store.investorProfile);

    const { role = '' } = useSelector((store: RootStateType) => store.auth);

    const { investorClientId = '', clientInterfaceCode, iwappRmId } = useSelector((store: RootStateType) => store.id);

    const { investorRmId = "" } = useSelector((store: RootStateType) => store.sessionData);

    const { selectedPortfolio, isPortfolioHeldaway } = useSelector((state: RootStateType) => state.selectedPortfiolio)

    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const history = useHistory()
    const { pathname: pathName, state } = history.location

    const navState = state as navigationData

    const onInvestorRecommendationsScreen = pathName === "/investor-recommendations"

    const [primaryCategoriesArray, setPrimaryCategoriesArray] = useState<Categories[]>(primaryCategories)
    const [secondaryCategoriesArray, setSecondaryCategoriesArray] = useState<Categories[]>(secondaryCategories)

    const [isFilterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);
    const [isFiltersLoading, setFiltersLoading] = useState<boolean>(false)
    const [amcFilterData, setAmcFilterData] = useState<AmcFilter[]>([])
    const [statusFilterData, setStatusFilterData] = useState<StatusFilter[]>([])
    const [searchText, setSearchText] = useState<string>("")
    const [isRecommendationDataLoading, setRecommendationsDataLoading] = useState<boolean>(false)
    const [recommendationsData, setRecommendationsData] = useState<RecommendationsData[]>([])
    const [recommendationsDataPaginationObj, setRecommendationsDataPaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0
    })

    const [isAddToCartDialogVisible, setAddToCartDialogVisible] = useState<boolean>(false)

    const [isFiveSchemesDialogVisible, setFiveSchemesDialogVisible] = useState<boolean>(false)

    const [isValidationDialogVisible, setValidationDialogVisible] = useState<boolean>(false)
    const [validationErrorsArray, setValidationErrorsArray] = useState<{ errorMessage: string; }[]>([])

    const debounceSearchText = useDebounce(searchText, 500);

    const fetchRecommendationsData = async ({
        primaryCategory,
        secondaryCategory,
        currentPage,
        rowsPerPage,
        appliedAmcFilters,
        appliedStatusFilters,
    }: {
        primaryCategory?: string;
        secondaryCategory?: string;
        currentPage: number;
        rowsPerPage: number;
        appliedAmcFilters?: string[];
        appliedStatusFilters?: string[];
    }) => {
        setRecommendationsDataLoading(true)

        const currentPrimaryCategory = primaryCategory ?? recommendationsActiveCategories.primaryCategory
        const currentSecondaryCategory = secondaryCategory ?? recommendationsActiveCategories.secondaryCategory

        const amcFilter = appliedRecommendationsFilters
            .filter(obj => obj.filterType.toLowerCase() === 'amc')
            .map(obj => `${obj.companyId}`)

        const statusFilter = appliedRecommendationsFilters
            .filter(obj => obj.filterType.toLowerCase() === 'status')
            .map(obj => `${obj.statusValue}`)

        let recommendedHeader = ""
        let buySellType = ""

        if (currentPrimaryCategory !== "New Purchase" && currentPrimaryCategory !== "Additional Purchase") {
            recommendedHeader = ""
            buySellType = currentPrimaryCategory
        } else {
            recommendedHeader = currentPrimaryCategory
            buySellType = currentSecondaryCategory
        }

        let modifiedSearchText = ""

        if (debounceSearchText?.trim()?.length >= 5) {
            modifiedSearchText = debounceSearchText?.trim() ?? ""
        }

        const payload = {
            clientCode: ![USER_ROLES.INVESTOR].includes(role) ? clientCode : investorClientId, // TO BE MADE DYNAMIC IN FUTURE
            // clientCode: [USER_ROLES.RM].includes(role)? clientCode : 'TW1000324', // TO BE MADE DYNAMIC IN FUTURE
            recommendedHeader,
            buySellType,
            amcFilter: appliedAmcFilters ?? amcFilter,
            statusFilter: appliedStatusFilters ?? statusFilter,
            searchText: modifiedSearchText,
            currentPage,
            rowsPerPage,
            portfolioId: selectedPortfolio?.value
        }

        const {
            newPurchase = {
                newPurchaseCount: 0,
                newLumpsumCount: 0,
                newSipCount: 0
            },
            additionalPurchase = {
                additionalPurchaseCount: 0,
                additionalLumpsumCount: 0,
                additionalSipCount: 0
            },
            swpCount = 0,
            stpCount = 0,
            switchCount = 0,
            redemptionCount = 0,
            FinalData: recommendationDataArray = []
        } = await (dispatch(getRecommendationsData(payload)) as unknown as Promise<GetRecommendationsDataResponseBody>)

        setPrimaryCategoriesArray([
            { label: "New Purchase", count: newPurchase.newPurchaseCount },
            { label: "Additional Purchase", count: additionalPurchase.additionalPurchaseCount },
            { label: "SWP", count: swpCount },
            { label: "STP", count: stpCount },
            { label: "Switch", count: switchCount },
            { label: "Redemption", count: redemptionCount }
        ])

        let lumpsumCount = 0
        let sipCount = 0

        if (currentPrimaryCategory === "New Purchase") {
            lumpsumCount = newPurchase.newLumpsumCount
            sipCount = newPurchase.newSipCount

            if (currentSecondaryCategory === "Lumpsum") {
                setRecommendationsDataPaginationObj({
                    currentPage: currentPage - 1,
                    rowsPerPage,
                    totalRows: newPurchase.newLumpsumCount
                })
            }
            if (currentSecondaryCategory === "SIP") {
                setRecommendationsDataPaginationObj({
                    currentPage: currentPage - 1,
                    rowsPerPage,
                    totalRows: newPurchase.newSipCount
                })
            }
        }

        if (currentPrimaryCategory === "Additional Purchase") {
            lumpsumCount = additionalPurchase.additionalLumpsumCount
            sipCount = additionalPurchase.additionalSipCount

            if (currentSecondaryCategory === "Lumpsum") {
                setRecommendationsDataPaginationObj({
                    currentPage: currentPage - 1,
                    rowsPerPage,
                    totalRows: additionalPurchase.additionalLumpsumCount
                })
            }
            if (currentSecondaryCategory === "SIP") {
                setRecommendationsDataPaginationObj({
                    currentPage: currentPage - 1,
                    rowsPerPage,
                    totalRows: additionalPurchase.additionalSipCount
                })
            }
        }

        if (currentPrimaryCategory === "SWP") {
            setRecommendationsDataPaginationObj({
                currentPage: currentPage - 1,
                rowsPerPage,
                totalRows: swpCount
            })
        } else if (currentPrimaryCategory === "STP") {
            setRecommendationsDataPaginationObj({
                currentPage: currentPage - 1,
                rowsPerPage,
                totalRows: stpCount
            })
        } else if (currentPrimaryCategory === "Switch") {
            setRecommendationsDataPaginationObj({
                currentPage: currentPage - 1,
                rowsPerPage,
                totalRows: switchCount
            })
        } else if (currentPrimaryCategory === "Redemption") {
            setRecommendationsDataPaginationObj({
                currentPage: currentPage - 1,
                rowsPerPage,
                totalRows: redemptionCount
            })
        }

        setSecondaryCategoriesArray([
            { label: "Lumpsum", count: lumpsumCount },
            { label: "SIP", count: sipCount }
        ])

        const modifiedRecommendationDataArray = recommendationDataArray.map((obj: RecommendationData) => {
            let recommendationDetails: RecommendationDetails[] = []

            if (currentPrimaryCategory === 'New Purchase' && currentSecondaryCategory === "Lumpsum") {
                recommendationDetails = [
                    { label: "Status", value: obj?.status ?? "-", color: colorMap[obj?.status?.toLowerCase() ?? ""] },
                    { label: "Recommended Amount", value: toINR({ value: obj?.recommendedAmount ?? "", locale, currencyCode }) },
                    { label: "Account", value: selectedPortfolio?.label ?? "-" },
                    { label: "Recommended Date", value: obj?.recommendedDate ?? "-" }
                ]
            } else if (currentPrimaryCategory === 'New Purchase' && currentSecondaryCategory === "SIP") {
                recommendationDetails = [
                    { label: "Status", value: obj?.status ?? "-", color: colorMap[obj?.status?.toLowerCase() ?? ""] },
                    { label: "Recommended Amount", value: toINR({ value: obj?.recommendedAmount ?? "", locale, currencyCode }) },
                    { label: (`${obj?.frequency ?? ""}`.toLowerCase() === "daily") ? "SIP Start Date" : "SIP Date", value: obj?.systematicDate ?? "-" },
                    ...((`${obj?.frequency ?? ""}`.toLowerCase() === "daily") ? [{ label: "SIP End Date", value: obj?.endDate ?? "-" }] : []),
                    { label: "Recommended Date", value: obj?.recommendedDate ?? "-" },
                    { label: "Frequency", value: obj?.frequency ?? "-" },
                    ...((`${obj?.frequency ?? ""}`.toLowerCase() !== "daily") ? [{ label: "No. of Installments", value: obj?.noOfInstallments ?? "-" }] : []),
                    { label: "Mandate", value: obj?.mandatesDisplayText ?? "-" },
                    { label: "Account", value: selectedPortfolio?.label ?? "-" }
                ]
            } else if (currentPrimaryCategory === 'Additional Purchase' && currentSecondaryCategory === "Lumpsum") {
                recommendationDetails = [
                    { label: "Status", value: obj?.status ?? "-", color: colorMap[obj?.status?.toLowerCase() ?? ""] },
                    { label: "Recommended Amount", value: toINR({ value: obj?.recommendedAmount ?? "", locale, currencyCode }) },
                    { label: "Folio", value: obj?.folioNo ?? "-" },
                    { label: "Recommended Date", value: obj?.recommendedDate ?? "-" },
                    { label: "Account", value: selectedPortfolio?.label ?? "-" }
                ]
            } else if (currentPrimaryCategory === 'Additional Purchase' && currentSecondaryCategory === "SIP") {
                recommendationDetails = [
                    { label: "Status", value: obj?.status ?? "-", color: colorMap[obj?.status?.toLowerCase() ?? ""] },
                    { label: "Recommended Amount", value: toINR({ value: obj?.recommendedAmount ?? "", locale, currencyCode }) },
                    { label: "Folio", value: obj?.folioNo ?? "-" },
                    { label: "Recommended Date", value: obj?.recommendedDate ?? "-" },
                    { label: (`${obj?.frequency ?? ""}`.toLowerCase() === "daily") ? "SIP Start Date" : "SIP Date", value: obj?.systematicDate ?? "-" },
                    ...((`${obj?.frequency ?? ""}`.toLowerCase() === "daily") ? [{ label: "SIP End Date", value: obj?.endDate ?? "-" }] : []),
                    { label: "Frequency", value: obj?.frequency ?? "-" },
                    ...((`${obj?.frequency ?? ""}`.toLowerCase() !== "daily") ? [{ label: "No. of Installments", value: obj?.noOfInstallments ?? "-" }] : []),
                    { label: "Mandate", value: obj?.mandatesDisplayText ?? "-" },
                    { label: "Account", value: selectedPortfolio?.label ?? "-" }
                ]
            } else if (currentPrimaryCategory === "SWP") {
                recommendationDetails = [
                    { label: "Status", value: obj?.status ?? "-", color: colorMap[obj?.status?.toLowerCase() ?? ""] },
                    { label: "Recommended Amount", value: toINR({ value: obj?.recommendedAmount ?? "", locale, currencyCode }) },
                    { label: "Folio", value: obj?.folioNo ?? "-" },
                    { label: "Recommended Date", value: obj?.recommendedDate ?? "-" },
                    { label: "SWP Date", value: obj?.systematicDate ?? "-" },
                    { label: "Frequency", value: obj?.frequency ?? "-" },
                    { label: "No. of Installments", value: obj?.noOfInstallments ?? "-" },
                    { label: "Bank", value: obj?.bankName ?? "-" },
                    { label: "Account", value: selectedPortfolio?.label ?? "-" }
                ]
            } else if (currentPrimaryCategory === "STP") {
                recommendationDetails = [
                    { label: "Status", value: obj?.status ?? "-", color: colorMap[obj?.status?.toLowerCase() ?? ""] },
                    { label: "Recommended Amount", value: toINR({ value: obj?.recommendedAmount ?? "", locale, currencyCode }) },
                    { label: "Folio", value: obj?.folioNo ?? "-" },
                    { label: "Recommended Date", value: obj?.recommendedDate ?? "-" },
                    { label: obj?.frequency === 'DAILY' ? "STP Start Date" : "STP Date", value: obj?.systematicDate ?? "-" },
                    ...(obj?.frequency === 'DAILY' ? [{ label: "STP End Date", value: obj?.endDate ?? "-" }] : []),
                    { label: "Frequency", value: obj?.frequency ?? "-" },
                    ...(obj?.frequency !== 'DAILY' ? [{ label: "No. of Installments", value: obj?.noOfInstallments ?? "-" }] : []),
                    { label: "Account", value: selectedPortfolio?.label ?? "-" }
                ].filter(Boolean)
            } else if (currentPrimaryCategory === "Switch") {
                recommendationDetails = [
                    { label: "Status", value: obj?.status ?? "-", color: colorMap[obj?.status?.toLowerCase() ?? ""] },
                    { label: obj?.recommendedUnits ? "Recommended Units" : "Recommended Amount", value: obj?.recommendedUnits ? obj?.recommendedUnits : toINR({ value: obj?.recommendedAmount ?? "", locale, currencyCode }) },
                    { label: "Folio", value: obj?.folioNo ?? "-" },
                    { label: "Recommended Date", value: obj?.recommendedDate ?? "-" },
                    { label: "Account", value: selectedPortfolio?.label ?? "-" }
                ]
            } else if (currentPrimaryCategory === "Redemption") {
                recommendationDetails = [
                    { label: "Status", value: obj?.status ?? "-", color: colorMap[obj?.status?.toLowerCase() ?? ""] },
                    { label: obj?.recommendedUnits ? "Recommended Units" : "Recommended Amount", value: obj?.recommendedUnits ? obj?.recommendedUnits : toINR({ value: obj?.recommendedAmount ?? "", locale, currencyCode }) },
                    { label: "Folio", value: obj?.folioNo ?? "-" },
                    { label: "Recommended Date", value: obj?.recommendedDate ?? "-" },
                    { label: "Account", value: selectedPortfolio?.label ?? "-" },
                    { label: "Bank", value: obj?.bankName ?? "-" }
                ]
            }

            const formattedExpiryDate = dayjs(obj?.expiryDate, "DD/MM/YYYY")

            return {
                id: obj.id,
                amcId: obj?.amcId ?? "",
                timeLeft: formattedExpiryDate.diff(today, 'day'),
                fromSchemeImg: obj?.imgUrl ?? "",
                fromSchemeName: obj?.schemeName ?? "",
                fromSchemeCategory: obj?.categoryName ?? "-",
                fromSchemeSubCategory: obj?.subCategoryName ?? "-",
                fromSchemeRisk: obj?.riskType ?? "",
                toSchemeImg: obj?.imgUrl ?? "",
                toSchemeName: obj?.toSchemeName ?? "",
                toSchemeCategory: obj?.toCategoryName ?? "-",
                toSchemeSubCategory: obj?.toSubCategoryName ?? "-",
                toSchemeRisk: obj?.toRiskType ?? "",
                aumAmount: obj?.aumAmount ?? "",
                trType: obj?.buySellType ?? "",
                categoryName: obj?.categoryName ?? "",
                exitLoad: obj?.exitLoad ?? "",
                navValue: obj?.navValue ?? "",
                amount: obj?.recommendedAmount && !Number.isNaN(Number(obj.recommendedAmount)) ? Number(obj.recommendedAmount) : "",
                riskType: obj?.riskType ?? "",
                schemeId: obj?.schemeId ?? "",
                schemeName: obj?.schemeName ?? "",
                subCategoryName: obj?.subCategoryName ?? "",
                y3Returns: obj?.y3Returns ?? "",
                status: obj?.status?.toLowerCase() ?? "",
                frequency: obj?.frequency ?? "",
                noOfInstallments: obj?.noOfInstallments && !Number.isNaN(Number(obj.noOfInstallments)) ? Number(obj.noOfInstallments) : "",
                mandateId: obj?.mandateId ?? "",
                mandatesDisplayText: obj?.mandatesDisplayText ?? "",
                sipDate: obj?.systematicDate ?? "",
                navDate: obj?.navDate ?? "",
                investedValue: obj?.investedValue ?? "",
                marketValue: obj?.marketValue ?? "",
                folioNo: obj?.folioNo ?? "",
                toCategoryName: obj?.toCategoryName ?? "",
                toRiskType: obj?.toRiskType ?? "",
                toSubCategoryName: obj?.toSubCategoryName ?? "",
                recommendationDetails,
                transMode: obj?.transMode ?? "",
                units: obj?.recommendedUnits ?? "",
                bankName: obj?.bankName ?? "",
                bankAccountNumber: obj?.bankAccountNumber ?? "",
                toSchemeId: obj?.toSchemeId ?? "",
                allUnitsFlag: obj?.allUnitsFlag ?? "",
                endDate: obj?.endDate ?? "",
                firstOrderFlag: obj?.firstOrderFlag ?? "",
            }
        })

        setRecommendationsData(modifiedRecommendationDataArray)
    }

    useEffect(() => {
        const fetchData = async () => {

            try {

                const appliedAmcFilters = appliedRecommendationsFilters
                    .filter((obj: AppliedRecommendationsFilters) => obj.filterType.toLowerCase() === "amc")
                    .map((obj: AppliedRecommendationsFilters) => `${obj.companyId}`)

                const appliedStatusFilters = appliedRecommendationsFilters
                    .filter((obj: AppliedRecommendationsFilters) => obj.filterType.toLowerCase() === "status")
                    .map((obj: AppliedRecommendationsFilters) => `${obj.statusId}`)

                setFiltersLoading(true)

                const amcFilterData = await (dispatch(getAmcFilters()) as unknown as Promise<AmcFilter[]>)
                const modifiedAmcFilterData = amcFilterData.map((obj: AmcFilter) => ({
                    ...obj,
                    companyId: `${obj.companyId}`,
                    checked: appliedAmcFilters.includes(`${obj.companyId}`)
                }))
                setAmcFilterData(modifiedAmcFilterData)

                const statusFilterData = await (dispatch(getStatusFilters()) as unknown as Promise<StatusFilter[]>)
                const modifiedStatusFilterData = statusFilterData.map((obj: StatusFilter) => ({
                    ...obj,
                    statusId: `${obj.statusId}`,
                    checked: appliedStatusFilters.includes(`${obj.statusId}`)
                }))
                setStatusFilterData(modifiedStatusFilterData)
            } catch (e) {
                console.log("Error: ", e)
            } finally {
                setFiltersLoading(false)
                setRecommendationsDataLoading(false)
            }
        }

        if ([USER_ROLES.INVESTOR].includes(role) && investorClientId) fetchData()
        else if (![USER_ROLES.INVESTOR].includes(role) && clientCode) fetchData()
    }, [clientCode, investorClientId])

    useEffect(() => {
        const fetchData = async () => {
            try {
                if ((debounceSearchText.trim().length === 0 || debounceSearchText.trim().length >= 5)
                    && !whiteSpaceOnlyRegex.test(debounceSearchText)) {
                    await fetchRecommendationsData({
                        currentPage: 1,
                        rowsPerPage: recommendationsDataPaginationObj.rowsPerPage,
                    })
                }
            } catch (e) {
                console.log("Error: ", e)
            } finally {
                setRecommendationsDataLoading(false)
            }
        }

        // fetchData()
        if ([USER_ROLES.INVESTOR].includes(role) && investorClientId) fetchData()
        else if (![USER_ROLES.INVESTOR].includes(role) && clientCode) fetchData()
    }, [debounceSearchText, investorClientId, clientCode])

    const setPrimaryCategory = async (category: string) => {
        try {
            if (category === "New Purchase" || category === "Additional Purchase") {
                dispatch(setRecommendationsActiveCategories({ primaryCategory: category, secondaryCategory: "Lumpsum" }))

                await fetchRecommendationsData({
                    currentPage: 1,
                    rowsPerPage: recommendationsDataPaginationObj.rowsPerPage,
                    primaryCategory: category,
                    secondaryCategory: "Lumpsum"
                })
            } else {
                dispatch(setRecommendationsActiveCategories({ primaryCategory: category, secondaryCategory: "" }))

                await fetchRecommendationsData({
                    currentPage: 1,
                    rowsPerPage: recommendationsDataPaginationObj.rowsPerPage,
                    primaryCategory: category,
                    secondaryCategory: ""
                })
            }
        } catch (e) {
            console.log("Error: ", e)
        } finally {
            setRecommendationsDataLoading(false)
        }
    }

    const setSecondaryCategory = async (category: string) => {
        try {
            dispatch(setRecommendationsActiveCategories({ ...recommendationsActiveCategories, secondaryCategory: category }));

            await fetchRecommendationsData({
                currentPage: 1,
                rowsPerPage: recommendationsDataPaginationObj.rowsPerPage,
                secondaryCategory: category
            })
        } catch (e) {
            console.log("Error: ", e)
        } finally {
            setRecommendationsDataLoading(false)
        }
    }

    const handleOpenFilterDrawer = () => {
        const appliedAmcFilters = appliedRecommendationsFilters
            .filter((obj: AppliedRecommendationsFilters) => obj.filterType.toLowerCase() === "amc")
            .map((obj: AppliedRecommendationsFilters) => `${obj.companyId}`)

        const appliedStatusFilters = appliedRecommendationsFilters
            .filter((obj: AppliedRecommendationsFilters) => obj.filterType.toLowerCase() === "status")
            .map((obj: AppliedRecommendationsFilters) => `${obj.statusId}`)

        setAmcFilterData((prevState: AmcFilter[]) => prevState.map((obj: AmcFilter) => ({
            ...obj,
            checked: appliedAmcFilters.includes(`${obj.companyId}`)
        })))

        setStatusFilterData((prevState: StatusFilter[]) => prevState.map((obj: StatusFilter) => ({
            ...obj,
            checked: appliedStatusFilters.includes(`${obj.statusId}`)
        })))

        setFilterDrawerOpen(true)

    }

    const handleCloseFilterDrawer = () => {
        setFilterDrawerOpen(false)
    }

    const handleAmcFilterChange = (value: string, checked: boolean) => {
        setAmcFilterData(prevState => prevState.map(
            item => item.companyId === value ? { ...item, checked } : item)
        )
    }

    const handleStatusFilterChange = (value: string, checked: boolean) => {
        setStatusFilterData(prevState => prevState.map(
            item => item.statusId === value ? { ...item, checked } : item)
        )
    }

    const handleApplyFilters = async () => {
        try {
            setFilterDrawerOpen(false)

            const appliedAmcFilters = amcFilterData
                .filter(obj => obj.checked)
                .map(obj => ({
                    label: obj.companyName,
                    value: obj.companyId,
                    filterType: "amc",
                    filterTypeValue: `amc-${obj.companyId}`,
                    companyId: obj.companyId,
                    companyName: obj.companyName
                }))

            const appliedStatusFilters = statusFilterData
                .filter(obj => obj.checked)
                .map(obj => ({
                    label: obj.statusValue,
                    value: obj.statusId,
                    filterType: "status",
                    filterTypeValue: `status-${obj.statusId}`,
                    statusId: obj.statusId,
                    statusValue: obj.statusValue
                }))

            dispatch(setAppliedRecommendationsFilters([...appliedAmcFilters, ...appliedStatusFilters]))

            const amcFiltersPayload = appliedAmcFilters
                .map(obj => `${obj.companyId}`)

            const statusFiltersPayload = appliedStatusFilters
                .map(obj => `${obj.statusValue}`)

            await fetchRecommendationsData({
                currentPage: 1,
                rowsPerPage: recommendationsDataPaginationObj.rowsPerPage,
                appliedAmcFilters: amcFiltersPayload,
                appliedStatusFilters: statusFiltersPayload
            })
        } catch (e) {
            console.log("Error: ", e)
        } finally {
            setRecommendationsDataLoading(false)
        }
    }

    const handleClearAll = async () => {
        try {
            setAmcFilterData(prevState => prevState.map(item => ({ ...item, checked: false })))
            setStatusFilterData(prevState => prevState.map(item => ({ ...item, checked: false })))

            if (appliedRecommendationsFilters.length) {
                await fetchRecommendationsData({
                    currentPage: 1,
                    rowsPerPage: recommendationsDataPaginationObj.rowsPerPage,
                    appliedAmcFilters: [],
                    appliedStatusFilters: []
                })
            }

            dispatch(setAppliedRecommendationsFilters([]))
        } catch (e) {
            console.log("Error: ", e)
        } finally {
            setRecommendationsDataLoading(false)
        }
    }

    const handleFilterLeavesClick = async (
        currentFilterType: string,
        currentFilterTypeValue: string,
        currentValue: string
    ) => {
        try {
            setRecommendationsDataLoading(true)

            const modifiedAppliedRecommendationsFilters = appliedRecommendationsFilters.filter(obj => obj.filterTypeValue !== currentFilterTypeValue)

            dispatch(setAppliedRecommendationsFilters(modifiedAppliedRecommendationsFilters))

            const amcFiltersPayload = modifiedAppliedRecommendationsFilters
                .filter(obj => obj.filterType.toLowerCase() === 'amc')
                .map(obj => `${obj.companyId}`)

            const statusFiltersPayload = modifiedAppliedRecommendationsFilters
                .filter(obj => obj.filterType.toLowerCase() === 'status')
                .map(obj => `${obj.statusValue}`)

            setAmcFilterData(prevState => prevState
                .map(obj => currentFilterType === "amc" && obj.companyId === currentValue ? { ...obj, checked: false } : obj)
            )

            setStatusFilterData(prevState => prevState
                .map(obj => currentFilterType === "status" && obj.statusId === currentValue ? { ...obj, checked: false } : obj)
            )

            await fetchRecommendationsData({
                currentPage: 1,
                rowsPerPage: recommendationsDataPaginationObj.rowsPerPage,
                appliedAmcFilters: amcFiltersPayload,
                appliedStatusFilters: statusFiltersPayload
            })
        } catch (e) {
            console.log("Error: ", e)
        } finally {
            setRecommendationsDataLoading(false)
        }
    }

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value?.length === 0) {
            setRecommendationsDataLoading(true)
        }

        setSearchText(event.target.value);
    }

    const handlePageChange = async (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        try {
            setRecommendationsDataLoading(true)

            await fetchRecommendationsData({
                currentPage: page + 1,
                rowsPerPage: recommendationsDataPaginationObj.rowsPerPage
            })
        } catch (e) {
            console.log("Error: ", e)
        } finally {
            setRecommendationsDataLoading(false)
        }
    }

    const handleRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            setRecommendationsDataLoading(true)

            await fetchRecommendationsData({
                currentPage: 1,
                rowsPerPage: Number(event.target.value)
            })
        } catch (e) {
            console.log("Error: ", e)
        } finally {
            setRecommendationsDataLoading(false)
        }
    }

    const handleClearSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
        setRecommendationsDataLoading(true)
        setSearchText("")
        e.preventDefault()
    }

    const handleAddToCartClick = async (data: AddToCartData) => {
        if (isPortfolioHeldaway) {
            dispatch(showError(HELDAWAY_PORTFOLIO_STATUS))
            return
        }

        try {
            setRecommendationsDataLoading(true)

            const isNewPurchaseLumpsum = `${recommendationsActiveCategories?.primaryCategory ?? ""}`.toLowerCase() === "new purchase" && `${recommendationsActiveCategories?.secondaryCategory ?? ""}`.toLowerCase() === "lumpsum"

            const isAddPurchaseLumpsum = `${recommendationsActiveCategories?.primaryCategory ?? ""}`.toLowerCase() === "additional purchase" && `${recommendationsActiveCategories?.secondaryCategory ?? ""}`.toLowerCase() === "lumpsum"

            let trType = data?.trType ?? ""

            if (data?.trType === 'XSIP') {
                trType = "SIP"
            } else if (isNewPurchaseLumpsum) {
                trType = "FRESH"
            } else if (isAddPurchaseLumpsum) {
                trType = "ADDITIONAL"
            }

            const addToCartResponse = await (dispatch(addToCart([
                {
                    aumAmount: data.aumAmount,
                    trType,
                    categoryName: data.categoryName,
                    clientCode: clientInterfaceCode ? clientInterfaceCode : investorClientId,
                    exitLoad: data.exitLoad,
                    navValue: data.navValue,
                    frequency: data.frequency,
                    amount: data.amount,
                    riskType: data.riskType,
                    schemeId: data.schemeId,
                    schemeName: data.schemeName,
                    subCategoryName: data.subCategoryName,
                    y3Returns: data.y3Returns,
                    noOfInstallment: data.noOfInstallments,
                    mandateId: data.mandateId,
                    sipDate: data.sipDate,
                    navDate: data.navDate,
                    investedValue: data.investedValue,
                    marketValue: data.marketValue,
                    folioNo: data.folioNo,
                    toSchemeName: data.toSchemeName,
                    toCategoryName: data.toCategoryName,
                    toRiskType: data.toRiskType,
                    toSubCategoryName: data.toSubCategoryName,
                    isRecommended: true,
                    recommedationId: data.id,
                    portfolioId: selectedPortfolio?.value,
                    transMode: data.transMode,
                    units: data.units,
                    bankName: data?.bankName,
                    bankAccountNumber: data?.bankAccountNumber,
                    toSchemeId: data?.toSchemeId,
                    allUnitsFlag: data?.allUnitsFlag,
                    amcId: data?.amcId,
                    rmId: `${investorRmId}`,
                    endDate: data?.endDate,
                    // ...(data?.trType === 'XSIP' ? {
                    ...(['XSIP', 'SWP', 'STP'].includes(`${data?.trType ?? ""}`.toUpperCase()) ? {
                        firstOrderFlag: data?.firstOrderFlag,
                    } : {})
                }
            ])) as unknown as Promise<AddToCartResponseBody>)

            if (addToCartResponse?.isSuccess) {
                await fetchRecommendationsData({
                    currentPage: recommendationsDataPaginationObj.currentPage + 1,
                    rowsPerPage: recommendationsDataPaginationObj.rowsPerPage,
                })

                const { isSuccess, cartItemsCount } = await (dispatch(getCartItemsCount({
                    clientCode: clientInterfaceCode ? clientInterfaceCode : investorClientId,
                    portfolioId: selectedPortfolio?.value
                })) as unknown as Promise<GetCartItemsCountResponseBody>)

                if (isSuccess) {
                    dispatch(setCartItemsCount(cartItemsCount));
                }

                setAddToCartDialogVisible(true)
            } else if (!addToCartResponse?.isSuccess) {
                if (addToCartResponse?.message) {
                    setFiveSchemesDialogVisible(true)
                } else {
                    setValidationErrorsArray(
                        addToCartResponse.response
                            .filter(data => !data.isSuccess)
                            .map(data => ({
                                errorMessage: data.message
                            }))
                    )
                    setValidationDialogVisible(true)
                }
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setRecommendationsDataLoading(false)
        }
    }

    const handleCloseAddToCartDialog = () => {
        setAddToCartDialogVisible(false)
    }

    const handleGoToCartClick = () => {
        history.push('/cart')
        setAddToCartDialogVisible(false)
    }

    const handleExploreClick = () => {
        dispatch(setAppliedFundsFilters([]));
        dispatch(setSelectedSchemesObj({}));
        dispatch(setExploreMutualFundsPageVisited(false));

        history.push(`${pathName}/explore`, {
            clientCode: navState?.clientCode || ""
        })
    }

    const handleCloseFiveSchemesDialog = () => {
        setFiveSchemesDialogVisible(false)
    }

    const handleValidationDialogClose = () => {
        setValidationDialogVisible(false)
    }

    const isFilterApplied = amcFilterData.some(obj => obj.checked) || statusFilterData.some(obj => obj.checked)

    return (
        <>
            <Box
                sx={(theme: Theme) => ({
                    borderRadius: "25px",
                    padding: "35px",
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                })}
            >
                {!onInvestorRecommendationsScreen && (
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            color: theme.palette.text.primary
                        })}
                    >
                        Recommendations
                    </Typography>
                )}
                <Box sx={{
                    marginTop: onInvestorRecommendationsScreen ? "0px" : "25px",
                    display: "flex",
                    columnGap: "15px",
                    alignItems: "center"
                }}>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            autoFocus
                            fullWidth
                            id="recommendations-search-input"
                            inputProps={{
                                'aria-label': 'Search recommendations',
                                'aria-describedby': 'recommendations-search-helper',
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            sx={{ display: searchText.length > 0 ? 'inline-flex' : 'none' }}
                                            onMouseDown={handleClearSearch}
                                            aria-label="Clear Search"
                                        >
                                            <DialogCloseIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: (theme: Theme) => ({
                                    borderRadius: '8px',
                                    height: '50px',
                                    border: `1px solid ${theme.palette.text.secondary}B2`,
                                    '&.Mui-focused': {
                                        outline: `2px solid ${theme.palette.primary[500]}`,
                                        outlineOffset: '2px',
                                    },
                                    '& fieldset': {
                                        border: 'none',
                                    },
                                    '& input': {
                                        paddingLeft: '10px',
                                        ...theme.typography.body_500,
                                        color: theme.palette.text.primary,
                                        '::placeholder': {
                                            ...theme.typography.subtitle_400,
                                            color: theme.palette.text.secondary,
                                            opacity: 1,
                                        },
                                    },
                                }),
                            }}
                            value={searchText}
                            placeholder="Search"
                            onChange={handleSearchTextChange}
                        />
                    </Box>
                    <Button
                        variant="text"
                        startIcon={
                            <FilterIcon
                                style={{ opacity: (isFiltersLoading || isRecommendationDataLoading) ? 0.5 : 1 }}
                            />
                        }
                        sx={(theme: Theme) => ({
                            color: theme.palette.text.primary,
                            padding: "5px",
                            ...theme.typography.body_500,
                            "&:hover": {
                                backgroundColor: theme.palette.grey[50]
                            }
                        })}
                        TouchRippleProps={{
                            style: {
                                color: theme.palette.grey[300]
                            }
                        }}
                        onClick={handleOpenFilterDrawer}
                        disabled={isFiltersLoading || isRecommendationDataLoading}
                    >
                        Filters
                    </Button>
                </Box>
                <Typography
                    id="recommendations-search-helper"
                    variant="overline_600"
                    sx={(theme: Theme) => ({
                        color: theme.palette.grey[800],
                        marginTop: "5px"
                    })}
                >
                    Minimum 5 characters required
                </Typography>
                <Box role="status" aria-live="polite" sx={visuallyHidden}>
                    {!isRecommendationDataLoading && recommendationsData.length === 0 && (searchText || appliedRecommendationsFilters.length > 0)
                        ? "No Result Found"
                        : ""}
                </Box>
                {/* )} */}
                <Box sx={{ mt: '25px' }}>
                    <Box
                        role="tablist"
                        sx={(theme: Theme) => ({
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            gap: '20px',
                            flexWrap: 'wrap',
                            paddingBottom: '15px',
                            borderBottom: `0.5px solid ${theme.palette.customColors.pastelGrey}`
                        })}>
                        {primaryCategoriesArray.map((
                            { label, count }: { label: string; count: number }
                        ) => (
                            <Button
                                key={label}
                                id={`recommendations-tab-${label.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                                role="tab"
                                aria-controls="recommendations-tabpanel"
                                aria-selected={recommendationsActiveCategories.primaryCategory === label}
                                sx={(theme: Theme) => ({
                                    padding: '3px 10px',
                                    background: recommendationsActiveCategories.primaryCategory === label ?
                                        theme.palette.primary[700] :
                                        theme.palette.common.white,
                                    color: recommendationsActiveCategories.primaryCategory === label ?
                                        theme.palette.common.white :
                                        theme.palette.text.secondary,
                                    borderRadius: '7px',
                                    "&:hover": {
                                        backgroundColor: recommendationsActiveCategories.primaryCategory === label ?
                                            theme.palette.primary[500] :
                                            'transparent',
                                        boxShadow: "none"
                                    }
                                })}
                                onClick={() => setPrimaryCategory(label)}
                                disabled={isRecommendationDataLoading}
                            >
                                <Typography
                                    component={'span'}
                                    variant="body_500"
                                    sx={{
                                        letterSpacing: '0.02px',
                                        mr: '0.3rem',
                                        lineHeight: '20px'
                                    }}
                                >
                                    {label}
                                </Typography>
                                <Typography
                                    component={'span'}
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        padding: '0px 10px',
                                        backgroundColor: recommendationsActiveCategories.primaryCategory === label ?
                                            theme.palette.common.white :
                                            theme.palette.primary[50],
                                        color: theme.palette.primary[700],
                                        borderRadius: '7px',
                                        letterSpacing: '0.02px',
                                        mt: '3px',
                                        mb: '3px',
                                        lineHeight: '20px'
                                    })}
                                >
                                    {count}
                                </Typography>
                            </Button>
                        ))
                        }
                    </Box>
                    <Box
                        id="recommendations-tabpanel"
                        role="tabpanel"
                        aria-labelledby={`recommendations-tab-${recommendationsActiveCategories.primaryCategory.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                    >
                        {(
                            recommendationsActiveCategories.primaryCategory === "New Purchase" ||
                            recommendationsActiveCategories.primaryCategory === "Additional Purchase"
                        ) && (
                                <>
                                    <Box sx={{ mt: '25px', ml: '10px' }}>
                                        <FormControl>
                                            <Typography
                                                component="span"
                                                role="status"
                                                aria-live="polite"
                                                aria-atomic="true"
                                                sx={visuallyHidden}
                                            >
                                                {`There are ${secondaryCategoriesArray.length} sub-categories under ${recommendationsActiveCategories.primaryCategory}. ${recommendationsActiveCategories.secondaryCategory || secondaryCategoriesArray[0]?.label || ""} is currently selected.`}
                                            </Typography>
                                            <Typography component="span" sx={visuallyHidden} id="secondary-categories-group-hint">
                                                Use the left and right arrow keys to switch between sub-categories.
                                            </Typography>
                                            <RadioGroup
                                                aria-label={`Sub-category filter, ${secondaryCategoriesArray.length} options`}
                                                aria-describedby="secondary-categories-group-hint"
                                                value={recommendationsActiveCategories.secondaryCategory}
                                                name="radio-buttons-group"
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'flex-start',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    flexDirection: 'row'
                                                }}
                                            >
                                                {secondaryCategoriesArray.map(({
                                                    label,
                                                    count
                                                }: {
                                                    label: string;
                                                    count: number
                                                }) => {

                                                    return (
                                                        <FormControlLabel
                                                            key={label}
                                                            value={label}
                                                            control={
                                                                <Radio
                                                                    size="small"
                                                                    inputProps={{
                                                                        'aria-label': `${label}, ${count} items`,
                                                                    }}
                                                                    sx={{
                                                                        "&.MuiRadio-sizeSmall": {
                                                                            padding: "0px"
                                                                        }
                                                                    }}
                                                                />
                                                            }
                                                            label={
                                                                <Box>
                                                                    <Typography
                                                                        component="span"
                                                                        variant="body_500"
                                                                        sx={(theme: Theme) => ({
                                                                            lineHeight: "20px",
                                                                            letterSpacing: '0.02px',
                                                                            color: recommendationsActiveCategories.secondaryCategory === label ?
                                                                                theme.palette.primary[800] :
                                                                                theme.palette.text.secondary,
                                                                        })}
                                                                    >
                                                                        {label}
                                                                    </Typography>

                                                                    <Typography
                                                                        component="span"
                                                                        variant="body_500"
                                                                        sx={(theme: Theme) => ({
                                                                            padding: '0px 10px',
                                                                            backgroundColor: recommendationsActiveCategories.secondaryCategory === label ?
                                                                                theme.palette.common.white :
                                                                                theme.palette.primary[50],
                                                                            color: recommendationsActiveCategories.secondaryCategory === label ?
                                                                                theme.palette.primary[500] :
                                                                                theme.palette.text.secondary,
                                                                            borderRadius: '7px',
                                                                            letterSpacing: '0.02px',
                                                                            lineHeight: '20px',
                                                                            marginLeft: "5px"
                                                                        })}
                                                                    >
                                                                        {count}
                                                                    </Typography>
                                                                </Box>
                                                            }
                                                            onChange={() => setSecondaryCategory(label)}
                                                            sx={(theme: Theme) => ({
                                                                backgroundColor: recommendationsActiveCategories.secondaryCategory === label ?
                                                                    theme.palette.primary[50] :
                                                                    theme.palette.common.white,
                                                                borderRadius: '10px',
                                                                padding: '8px 10px 8px 15px',
                                                                border: recommendationsActiveCategories.secondaryCategory === label ?
                                                                    `1px solid ${theme.palette.primary[500]}` :
                                                                    `1px solid ${theme.palette.text.secondary}80`,
                                                                "&.MuiFormControlLabel-root": {
                                                                    columnGap: "10px"
                                                                },
                                                            })}
                                                            disabled={isRecommendationDataLoading}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </FormControl>
                                    </Box>
                                    {/* )} */}
                                </>
                            )}
                        <Box
                            sx={{
                                marginTop: "25px",
                                display: appliedRecommendationsFilters.length ? "flex" : "none",
                                gap: "15px",
                                flexWrap: "wrap",
                            }}
                        >
                            {appliedRecommendationsFilters.map((obj) => (
                                <Button
                                    key={obj.filterTypeValue}
                                    variant="contained"
                                    startIcon={<CrossIcon />}
                                    onClick={() => handleFilterLeavesClick(
                                        obj.filterType,
                                        obj.filterTypeValue,
                                        obj.value
                                    )}
                                    sx={(theme: Theme) => ({
                                        borderRadius: "61px",
                                        backgroundColor: theme.palette.primary[50],
                                        padding: "7px 16px",
                                        color: theme.palette.text.primary,
                                        whiteSpace: "nowrap",
                                        boxShadow: "none",
                                        ...theme.typography.subtitle_400,
                                        "& .MuiButton-startIcon": {
                                            paddingTop: "2px"
                                        },
                                        "&:hover": {
                                            backgroundColor: theme.palette.primary[100],
                                            boxShadow: "none"
                                        },
                                        "&.Mui-disabled": {
                                            opacity: 0.5
                                        }
                                    })}
                                    disabled={isRecommendationDataLoading}
                                >
                                    {obj.label}
                                </Button>
                            ))}
                        </Box>
                        <Box sx={{ mt: '25px' }}>
                            {isRecommendationDataLoading && (
                                [...Array(5)].map((_, index) => (
                                    <Box
                                        key={index}
                                        sx={{ marginBottom: "35px" }}
                                    >
                                        <Box
                                            sx={(theme: Theme) => ({
                                                padding: '25px',
                                                background: theme.palette.grey[50],
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center"
                                            })}>
                                            <Box>
                                                <Skeleton
                                                    variant="rounded"
                                                    sx={{
                                                        display: "inline-block",
                                                        borderRadius: '30px',
                                                        marginBottom: '5px',
                                                    }}
                                                    width={70}
                                                    height={18}
                                                    animation="wave"
                                                />
                                                <Grid
                                                    container
                                                    sx={{
                                                        alignItems: 'center',
                                                        columnGap: "55px",
                                                        rowGap: "20px",
                                                        flexWrap: "wrap",
                                                    }}>
                                                    <Grid
                                                        item
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            columnGap: "15px",
                                                        }}
                                                    >
                                                        <Skeleton
                                                            variant="rounded"
                                                            width={50}
                                                            height={50}
                                                            sx={{ borderRadius: '8px' }}
                                                            animation="wave"
                                                        />
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                rowGap: "3px"
                                                            }}
                                                        >
                                                            <Skeleton
                                                                sx={{ fontSize: '18px' }}
                                                                animation="wave"
                                                                width={200}
                                                            />
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    columnGap: "8px",
                                                                    alignItems: "center"
                                                                }}
                                                            >
                                                                <Skeleton
                                                                    sx={{ fontSize: '16px' }}
                                                                    animation="wave"
                                                                    width={40}
                                                                />
                                                                <Skeleton
                                                                    variant="rounded"
                                                                    width={3}
                                                                    height={3}
                                                                    animation="wave"
                                                                    sx={{ borderRadius: "50%" }}
                                                                />
                                                                <Skeleton
                                                                    sx={{ fontSize: '16px' }}
                                                                    animation="wave"
                                                                    width={40}
                                                                />
                                                                <Skeleton
                                                                    variant="rounded"
                                                                    width={3}
                                                                    height={3}
                                                                    animation="wave"
                                                                    sx={{ borderRadius: "50%" }}
                                                                />
                                                                <Skeleton
                                                                    sx={{ fontSize: '16px' }}
                                                                    animation="wave"
                                                                    width={40}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Grid >
                                                    {(recommendationsActiveCategories.primaryCategory === "STP" || recommendationsActiveCategories.primaryCategory === "Switch") && (
                                                        <>
                                                            <Grid item>
                                                                <Skeleton
                                                                    variant="text"
                                                                    sx={{ fontSize: "10px" }}
                                                                    animation="wave"
                                                                    width={30}
                                                                />
                                                            </Grid>
                                                            <Grid
                                                                item
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    columnGap: "15px",
                                                                }}
                                                            >
                                                                <Skeleton
                                                                    variant="rounded"
                                                                    width={50}
                                                                    height={50}
                                                                    sx={{ borderRadius: '8px' }}
                                                                    animation="wave"
                                                                />
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        flexDirection: "column",
                                                                        rowGap: "3px"
                                                                    }}
                                                                >
                                                                    <Skeleton
                                                                        sx={{ fontSize: '18px' }}
                                                                        animation="wave"
                                                                        width={40}
                                                                    />
                                                                    <Box
                                                                        sx={{
                                                                            display: "flex",
                                                                            columnGap: "8px",
                                                                            alignItems: "center"
                                                                        }}
                                                                    >
                                                                        <Skeleton
                                                                            sx={{ fontSize: '16px' }}
                                                                            animation="wave"
                                                                            width={40}
                                                                        />
                                                                        <Skeleton
                                                                            variant="rounded"
                                                                            width={3}
                                                                            height={3}
                                                                            animation="wave"
                                                                            sx={{
                                                                                borderRadius: "50%",
                                                                            }}
                                                                        />
                                                                        <Skeleton
                                                                            sx={{ fontSize: '16px' }}
                                                                            animation="wave"
                                                                            width={40}
                                                                        />
                                                                        <Skeleton
                                                                            variant="rounded"
                                                                            width={3}
                                                                            height={3}
                                                                            animation="wave"
                                                                            sx={{ borderRadius: "50%" }}
                                                                        />
                                                                        <Skeleton
                                                                            sx={{ fontSize: '16px' }}
                                                                            animation="wave"
                                                                            width={40}
                                                                        />
                                                                    </Box>
                                                                </Box>
                                                            </Grid >
                                                        </>
                                                    )}
                                                </Grid>
                                            </Box>
                                            {role === USER_ROLES.INVESTOR && (
                                                <Skeleton
                                                    variant="rounded"
                                                    animation="wave"
                                                    sx={{ borderRadius: "5px" }}
                                                    width={124}
                                                    height={37.5}
                                                />
                                            )}
                                        </Box>
                                        <Grid container>
                                            {[...Array(8)].map((_, index) => (
                                                <Grid
                                                    key={index}
                                                    item
                                                    xs={12}
                                                    sm={3}
                                                    sx={{ padding: "20px" }}
                                                >
                                                    <Skeleton
                                                        sx={{ fontSize: '16px' }}
                                                        animation="wave"
                                                        width={100}
                                                    />
                                                    <Skeleton
                                                        sx={{ fontSize: '16px' }}
                                                        animation="wave"
                                                        width={100}
                                                    />
                                                </Grid>
                                            ))}

                                        </Grid>
                                    </Box >
                                ))
                            )}
                            {!isRecommendationDataLoading && recommendationsData.length === 0 && (
                                <Box sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    marginTop: "60px"
                                }}>
                                    {![USER_ROLES.INVESTOR].includes(role) && (
                                        (((searchText ?? "").length !== 0) ||
                                            (appliedRecommendationsFilters.length > 0)) ? (
                                            <Typography
                                                variant="body_600"
                                                sx={(theme: Theme) => ({
                                                    color: theme.palette.text.primary
                                                })}
                                            >
                                                No Result Found
                                            </Typography>
                                        ) : (
                                            <Box sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                flexDirection: "column"
                                            }}>
                                                <Box
                                                    component="img"
                                                    src={"/images/no-recommendations-data.svg"}
                                                    alt="No Recommendations"
                                                    sx={{
                                                        width: "188.34px",
                                                        height: "171.83px",
                                                        objectFit: "contain"
                                                    }}
                                                />
                                                <Typography
                                                    variant="h1"
                                                    sx={(theme: Theme) => ({
                                                        marginTop: "25px",
                                                        lineHeight: "32.5px",
                                                        color: theme.palette.text.primary
                                                    })}
                                                >
                                                    {/* Haven’t made any recommendations yet? */}
                                                    No recommendations in {recommendationsActiveCategories?.secondaryCategory?.length ? `${recommendationsActiveCategories?.primaryCategory ?? ""} (${recommendationsActiveCategories?.secondaryCategory ?? ""})` : recommendationsActiveCategories?.primaryCategory ?? ""}
                                                </Typography>
                                                <Typography
                                                    variant="subtitle_400"
                                                    sx={(theme: Theme) => ({
                                                        marginTop: "8px",
                                                        lineHeight: "24px",
                                                        color: theme.palette.text.secondary
                                                    })}
                                                >
                                                    Let’s begin recommending now!
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    sx={(theme: Theme) => ({
                                                        lineHeight: "22.5px",
                                                        letterSpacing: "0.02px",
                                                        backgroundColor: theme.palette.primary[500],
                                                        color: theme.palette.textDark.primary,
                                                        marginTop: "25px",
                                                        padding: "13.5px 91.5px"
                                                    })}
                                                    onClick={handleExploreClick}
                                                >
                                                    Explore
                                                </Button>
                                            </Box>
                                        ))}
                                    {role === USER_ROLES.INVESTOR && (
                                        <Typography
                                            variant="body_600"
                                            sx={(theme: Theme) => ({
                                                color: theme.palette.text.primary
                                            })}
                                        >
                                            No Result Found
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {!isRecommendationDataLoading && (
                                recommendationsData.map((obj: RecommendationsData) =>
                                    <RecommendationInfoCard
                                        key={obj.id}
                                        handleAddToCartClick={handleAddToCartClick}
                                        {...obj}
                                    />
                                ))
                            }
                        </Box>
                        {!isRecommendationDataLoading && recommendationsData.length > 0 && (
                            <TablePaginationComponent
                                tableDataCount={recommendationsDataPaginationObj.totalRows}
                                handlePageChange={handlePageChange}
                                handleRowsPerPageChange={handleRowsPerPageChange}
                                rowsPerPageOptions={[5, 10, 15, 20]}
                                rowsPerPage={recommendationsDataPaginationObj.rowsPerPage}
                                currentPage={recommendationsDataPaginationObj.currentPage}
                            />
                        )}
                    </Box>
                </Box>
            </Box >
            <Drawer
                open={isFilterDrawerOpen}
                onClose={handleCloseFilterDrawer}
                anchor="right"
                PaperProps={{
                    role: 'dialog',
                    'aria-modal': true,
                    'aria-labelledby': 'filter-drawer-title',
                    'aria-label': 'Filters',
                    'aria-describedby': 'filter-drawer-description',
                    sx: {
                        padding: "25px",
                        width: "450px"
                    }
                }}
                ModalProps={{
                    sx: (theme: Theme) => ({
                        backgroundColor: `${theme.palette.common.black}03`
                    })
                }}
            >
                <Typography id="filter-drawer-description" sx={visuallyHidden}>
                    Apply or clear filters to refine the recommendations.
                </Typography>
                <CustomFilter
                    isLoading={isFiltersLoading}
                    handleClearAll={handleClearAll}
                    handleApplyFilters={handleApplyFilters}
                    showCloseIcon={true}
                    handleCloseIconClick={handleCloseFilterDrawer}
                    amcFilterData={amcFilterData}
                    handleAmcFilterChange={handleAmcFilterChange}
                    showCategoryFilter={false}
                    showRiskFilter={false}
                    showStatusFilter={true}
                    statusFilterData={statusFilterData}
                    handleStatusFilterChange={handleStatusFilterChange}
                    isFilterApplied={isFilterApplied}
                    defaultExpandedFilter="amc"
                />
            </Drawer>
            <Dialog
                onClose={handleCloseAddToCartDialog}
                open={isAddToCartDialogVisible}
                maxWidth='sm'
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '15px',
                        backgroundColor: theme.palette.common.white,
                        padding: "24px",
                        width: "533px",
                    },
                })}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "40px"
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            rowGap: "26px"
                        }}
                    >
                        <CartDialogIcon />
                        <Typography
                            variant="h1"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            Added to Cart Successfully!
                        </Typography>
                    </Box>
                    <DialogActions
                        sx={{ columnGap: "6px" }}
                    >
                        <Button
                            variant="outlined"
                            sx={(theme: Theme) => ({
                                ...theme.typography.body_600,
                                fontSize: "16px",
                                color: theme.palette.text.primary,
                                border: `1px solid ${theme.palette.customColors.pastelGrey}`,
                                borderRadius: "4px",
                                padding: "8px 56px",
                                flex: 1,
                                ":hover": {
                                    backgroundColor: theme.palette.grey[50],
                                    borderColor: theme.palette.grey[200],
                                }
                            })}
                            onClick={handleCloseAddToCartDialog}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            sx={(theme: Theme) => ({
                                ...theme.typography.body_700,
                                color: theme.palette.textDark.primary,
                                backgroundColor: theme.palette.primary[500],
                                borderRadius: "4px",
                                padding: "6px 56px",
                                flex: 1
                            })}
                            onClick={handleGoToCartClick}
                        >
                            Go to Cart
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog >
            <Dialog
                onClose={handleCloseFiveSchemesDialog}
                open={isFiveSchemesDialogVisible}
                maxWidth='sm'
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        backgroundColor: theme.palette.common.white,
                        padding: "24px",
                        width: "533px",
                    },
                })}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        alignItems: "center",
                        rowGap: "48px"
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            rowGap: "14px",
                            paddingTop: "15px"
                        }}
                    >
                        <Box
                            component="img"
                            src="/images/max-cart-items.svg"
                            alt="Max cart items"
                            sx={{ objectFit: "contain" }}
                        />
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: "25px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary
                            })}
                        >
                            You can add only {MAX_SCHEMES_SELECTED} schemes at a time !
                        </Typography>
                        <Typography
                            variant="subtitle_400"
                            sx={(theme: Theme) => ({
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.secondary
                            })}
                        >
                            Cart cannot contain more than {MAX_SCHEMES_SELECTED} schemes
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        fullWidth
                        sx={(theme: Theme) => ({
                            padding: "13px 20px",
                            borderRadius: "4px",
                            backgroundColor: theme.palette.primary[500],
                            fontWeight: 600,
                            fontSize: "16px",
                            lineHeight: "20px"
                        })}
                        onClick={handleCloseFiveSchemesDialog}
                    >
                        Okay
                    </Button>
                </Box>
            </Dialog>
            <Dialog
                onClose={handleValidationDialogClose}
                open={isValidationDialogVisible}
                maxWidth='sm'
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        backgroundColor: theme.palette.common.white,
                        padding: "24px",
                        width: "533px",
                    },
                })}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        rowGap: "30px"
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            rowGap: "14px",
                            paddingTop: "15px"
                        }}
                    >
                        <Box
                            component="img"
                            src="/images/error.svg"
                            alt="Error"
                            sx={{ objectFit: "contain" }}
                        />
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: "25px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary
                            })}
                        >
                            Failed!
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            maxHeight: "350px",
                            overflowY: "auto",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: validationErrorsArray.length === 1 ? "center" : "flex-start",
                            alignItems: "center",
                            rowGap: "10px"
                        }}
                    >
                        {validationErrorsArray.map((obj: { errorMessage: string }, index: number) => (
                            <Box
                                key={index}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center"
                                }}
                            >
                                <Typography

                                    variant="subtitle_400"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.secondary,
                                        paddingBottom: "10px",
                                    })}
                                >
                                    {obj.errorMessage.charAt(0).toUpperCase() + obj.errorMessage.slice(1)}
                                </Typography>
                                {validationErrorsArray.length - 1 !== index && (
                                    <Box
                                        sx={(theme: Theme) => ({
                                            width: "30%",
                                            border: `0.5px dashed ${theme.palette.text.secondary}`
                                        })}
                                    />
                                )}
                            </Box>
                        ))}
                    </Box>
                    <Button
                        variant="contained"
                        fullWidth
                        sx={(theme: Theme) => ({
                            padding: "13px 20px",
                            borderRadius: "4px",
                            backgroundColor: theme.palette.primary[500],
                            fontWeight: 600,
                            fontSize: "16px",
                            lineHeight: "20px"
                        })}
                        onClick={handleValidationDialogClose}
                    >
                        Okay
                    </Button>
                </Box>
            </Dialog >
        </>
    )
}